import React, { useState } from "react";
import LandingPopupDemo from "./landingPopupDemo";
import CustomModal from "../CustomModal/CustomModal";
import { FiCalendar, FiShield, FiTrendingUp, FiUsers, FiArrowRight, FiCheckCircle } from "react-icons/fi";

export const LandingHeroSection: React.FC = () => {
  const [showPopUp, setShowPopUp] = useState(false);

  return (
    <section className="relative overflow-hidden bg-slate-900 text-white pt-12 pb-24 lg:pt-20 lg:pb-32">
      {/* Dynamic Background Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Hero Content */}
          <div className="lg:col-span-7 space-y-8 text-center lg:text-left" data-aos="fade-right">
            
            {/* Pill Tag */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-black uppercase tracking-widest backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              Next-Gen School & Childcare Management Platform
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight">
              Empowering Education through{" "}
              <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-teal-300 bg-clip-text text-transparent">
                Digital Innovation
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-base sm:text-lg text-slate-300 font-normal leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Transform administrative operations, empower educators, streamline fee collection, and keep parents connected with real-time updates and secure pickup authorization.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
              <button
                type="button"
                onClick={() => setShowPopUp(true)}
                className="w-full sm:w-auto px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-xl shadow-blue-500/25 transition-all flex items-center justify-center gap-2 group"
              >
                <FiCalendar className="text-base group-hover:scale-110 transition-transform" />
                Book A Demo Today
              </button>

              <a
                href="#about"
                className="w-full sm:w-auto px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest bg-slate-800/80 hover:bg-slate-800 text-slate-200 border border-slate-700/80 transition-all flex items-center justify-center gap-2"
              >
                Explore Platform <FiArrowRight size={14} />
              </a>
            </div>

            {/* Trust highlights */}
            <div className="pt-6 border-t border-slate-800/80 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs text-slate-400 font-medium">
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-emerald-400" /> Automated Invoicing & Receipting
              </div>
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-emerald-400" /> Authorized Student Pickups
              </div>
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-emerald-400" /> Real-Time Parent App
              </div>
            </div>
          </div>

          {/* Right Hero Image / Dashboard Preview */}
          <div className="lg:col-span-5 relative" data-aos="fade-left">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Glassmorphic Background Card */}
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-indigo-600/20 rounded-[2.5rem] transform rotate-3 blur-sm" />
              
              <div className="relative bg-slate-800/90 border border-slate-700/80 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
                
                {/* Visual Header */}
                <div className="flex items-center justify-between pb-6 border-b border-slate-700/60 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold">
                      <FiShield size={20} />
                    </div>
                    <div>
                      <h4 className="font-black text-white text-sm">Pendulum Operating Portal</h4>
                      <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">● Live System Active</p>
                    </div>
                  </div>
                  <span className="text-[10px] bg-slate-700 text-slate-300 px-3 py-1 rounded-full font-bold">
                    v2.4 Ready
                  </span>
                </div>

                {/* Dashboard Stats Cards */}
                <div className="space-y-4">
                  <div className="bg-slate-900/90 rounded-2xl p-4 border border-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center">
                        <FiUsers size={18} />
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">Student Engagement</span>
                        <h4 className="text-lg font-black text-white">99.4% Active Attendance</h4>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg">
                      +14%
                    </span>
                  </div>

                  <div className="bg-slate-900/90 rounded-2xl p-4 border border-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
                        <FiTrendingUp size={18} />
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">School Operations</span>
                        <h4 className="text-lg font-black text-white">Automated Result Sheets</h4>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-blue-400 bg-blue-500/10 px-2.5 py-1 rounded-lg">
                      Instant
                    </span>
                  </div>
                </div>

                {/* Testimonial Quote */}
                <div className="mt-6 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 p-4 rounded-2xl border border-blue-500/20 text-xs italic text-slate-300 space-y-2">
                  <p>
                    "Pendulum Digital empowers educators, pupils, and parents by providing real-time technology solution that simplifies school operations."
                  </p>
                  <div className="flex justify-between items-center text-[10px] not-italic font-bold text-slate-400 pt-1">
                    <span>— Pendulum Mission Statement</span>
                    <span className="text-blue-400 font-black">Digital Education</span>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Book a Demo Modal */}
      {showPopUp && (
        <CustomModal
          title="Book a Free Demo"
          width={75}
          hideModal={() => setShowPopUp(false)}
        >
          <LandingPopupDemo />
        </CustomModal>
      )}
    </section>
  );
};

export default LandingHeroSection;
